import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { X, Loader2, MessageSquareText, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PhotoImg from "@/components/PhotoImg";
import { useModalBack } from "@/lib/modalBack";

// One-tap customer update (Flow C): preview of the drafted note + 3 best photos
// + the branded no-login portal link. Send by text (sms: prefilled) or email
// (existing mail service). No customer on the job → add phone/email inline.
export default function CustomerUpdateSheet({ open, onClose, projectId, onSent }) {
  const { t } = useTranslation();
  const [pv, setPv] = useState(null);
  const [note, setNote] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState("");
  const [whatsNext, setWhatsNext] = useState("");      // Batch A: "what's next" line
  const [includeSched, setIncludeSched] = useState(false); // Batch A: schedule PDF link option
  useModalBack(open, onClose);

  useEffect(() => {
    if (!open || !projectId) return;
    setPv(null);
    api.get(`/update360/customer-update/${projectId}/preview`).then(({ data }) => {
      setPv(data);
      setNote(data.note || "");
      setWhatsNext((data.next_tasks || []).map((tk) => tk.title).filter(Boolean).join("; "));
      setName(data.customer?.name || "");
      setPhone(data.customer?.phone || "");
      setEmail(data.customer?.email || "");
    }).catch(() => toast.error(t("bg_error")));
  }, [open, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const send = async (channel) => {
    if (busy) return;
    if (channel === "sms" && !phone.trim()) { toast.error(t("cu_missing_contact")); return; }
    if (channel === "email" && !email.trim()) { toast.error(t("cu_missing_contact")); return; }
    setBusy(channel);
    try {
      let finalNote = note.trim();
      if (whatsNext.trim()) finalNote += `\n\n${t("cu_whats_next_label", "What's next")}: ${whatsNext.trim()}`;
      if (includeSched) {
        const { data: ss } = await api.post(`/reports/project-status/${projectId}/share`, { show_badge: true, show_delays: true });
        finalNote += `\n${t("share_sched_report", "Schedule report (customer-safe PDF)")}: ${window.location.origin}${ss.path}`;
      }
      const { data } = await api.post("/update360/customer-update/send", {
        project_id: projectId, channel, note: finalNote,
        customer_name: name.trim(), customer_phone: phone.trim(), customer_email: email.trim(),
        photo_ids: pv?.photo_ids || [], token: pv?.token || "",
      });
      toast.success(t("cu_sent"));
      onSent?.(data);
      if (channel === "sms") {
        const body = encodeURIComponent(`${finalNote}\n${data.portal_url}`);
        window.location.href = `sms:${phone.trim()}?&body=${body}`;
      }
      onClose();
    } catch (e) {
      toast.error(t("toast_failed"));
    } finally { setBusy(""); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/50" onClick={onClose} data-testid="cu-sheet">
      <div className="max-h-[88dvh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-white p-5 pb-[calc(20px+env(safe-area-inset-bottom,0px))]"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-base font-extrabold text-slate-900">{t("cu_send_title")}</h3>
          <button onClick={onClose} data-testid="cu-close" aria-label={t("action_cancel")}
            className="flex min-h-[40px] min-w-[40px] items-center justify-center rounded-full text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        {!pv ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : (
          <div className="mt-3 space-y-3">
            {!pv.has_customer ? (
              <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="cu-add-customer">
                <p className="text-xs font-bold text-amber-800">{t("cu_add_customer")}</p>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("cu_name_ph")}
                  data-testid="cu-name" className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" type="tel"
                  data-testid="cu-phone" className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email"
                  data-testid="cu-email" className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
              </div>
            ) : (
              <p className="text-xs font-semibold text-slate-500" data-testid="cu-to">
                {name}{phone ? ` · ${phone}` : ""}{!phone && email ? ` · ${email}` : ""}
              </p>
            )}
            <label className="block">
              <span className="text-xs font-bold text-slate-600">{t("cu_note_label")}</span>
              <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="cu-note"
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-bold text-slate-600">{t("cu_whats_next_label", "What's next")}</span>
              <textarea rows={2} value={whatsNext} onChange={(e) => setWhatsNext(e.target.value)} data-testid="cu-whats-next"
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-sm font-semibold text-slate-700" data-testid="cu-sched-row">
              <input type="checkbox" checked={includeSched} onChange={(e) => setIncludeSched(e.target.checked)} data-testid="cu-sched-pdf" className="h-5 w-5 accent-brand" />
              {t("cu_sched_pdf", "Include schedule PDF link")}
            </label>
            {pv.photo_ids?.length > 0 && (
              <div className="grid grid-cols-3 gap-2" data-testid="cu-photos">
                {pv.photo_ids.map((pid) => (
                  <div key={pid} className="aspect-square overflow-hidden rounded-xl bg-slate-100">
                    <PhotoImg photoId={pid} alt="" thumb className="h-full w-full object-cover" />
                  </div>
                ))}
              </div>
            )}
            <div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t("cu_link_label")}</span>
              <p className="truncate rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs text-slate-500" data-testid="cu-link">{pv.portal_url}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => send("sms")} disabled={!!busy} data-testid="cu-send-text"
                className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full bg-brand px-4 text-sm font-bold text-white active:scale-95 disabled:opacity-60">
                {busy === "sms" ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquareText className="h-4 w-4" />}
                {t("cu_send_text")}
              </button>
              <button onClick={() => send("email")} disabled={!!busy} data-testid="cu-send-email"
                className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full border border-slate-300 px-4 text-sm font-bold text-slate-700 active:scale-95 disabled:opacity-60">
                {busy === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                {t("cu_send_email")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
